import { useState, useEffect, useMemo, useCallback } from "react";
import { TreeViewBaseItem } from "@mui/x-tree-view/models";
import { useTreeRelations } from "./useTreeRelations";

export type SelectionRules = {
  cascadeChildren: boolean;
  autoSelectParents: boolean;
  disableChildrenWhenParentSelected: boolean;
  collapseChipsToAncestors: boolean;
  showIndeterminateCheckbox: boolean;
  excludeParentsFromSelection: boolean;
};

export const SELECTION_PRESETS = {
  hierarchical: {
    cascadeChildren: true,
    autoSelectParents: true,
    disableChildrenWhenParentSelected: true,
    collapseChipsToAncestors: true,
    showIndeterminateCheckbox: true,
    excludeParentsFromSelection: false,
  },
  independent: {
    cascadeChildren: false,
    autoSelectParents: false,
    disableChildrenWhenParentSelected: false,
    collapseChipsToAncestors: false,
    showIndeterminateCheckbox: false,
    excludeParentsFromSelection: false,
  },
  flexible: {
    cascadeChildren: true,
    autoSelectParents: true,
    disableChildrenWhenParentSelected: false,
    collapseChipsToAncestors: false,
    showIndeterminateCheckbox: true,
    excludeParentsFromSelection: true,
  },
} as const;

/**
 * Hook that manages hierarchical selection behavior for tree items
 * @param items Tree items to manage
 * @param initialSelectedItems Initial selected item IDs
 * @param onSelectionChange Callback for selection changes
 * @param selectionRules Control how parent-child selection behaves
 * @returns Object with selection state and handlers
 */
export function useHierarchicalSelection(
  items: TreeViewBaseItem[],
  initialSelectedItems: string[] = [],
  onSelectionChange?: (selectedItems: string[]) => void,
  selectionRules: SelectionRules = SELECTION_PRESETS.hierarchical
) {
  // Internal state for selected items
  const [selectedItems, setSelectedItems] =
    useState<string[]>(initialSelectedItems);
  // Get tree relationship utilities
  const { itemsMap, childrenMap, getAllDescendants, filterToHighestAncestors } =
    useTreeRelations(items);

  // Update internal state when external selection changes
  useEffect(() => {
    setSelectedItems(initialSelectedItems);
  }, [initialSelectedItems]);

  // Create a reusable filter function for parent exclusion
  const filterParentNodes = useCallback(
    (items: string[]): string[] => {
      if (!selectionRules.excludeParentsFromSelection) {
        return items;
      }
      // Filter out any item that has children (is a parent)
      return items.filter((id) => {
        const hasChildren = (childrenMap.get(id)?.length || 0) > 0;
        return !hasChildren;
      });
    },
    [childrenMap, selectionRules.excludeParentsFromSelection]
  );

  // Get output items that should be passed to parent component
  // This filters out parent nodes when excludeParentsFromSelection is enabled
  const outputSelectedItems = useMemo(
    () => filterParentNodes(selectedItems),
    [selectedItems, filterParentNodes]
  );

  // Get disabled items (children of selected parents - based on rules)
  const disabledItemsSet = useMemo(() => {
    // Check if children should be disabled when parent is selected
    if (!selectionRules.disableChildrenWhenParentSelected) {
      return new Set<string>();
    }
    // Collect all children of selected parents
    const disabledChildrenIds = selectedItems
      .filter((id) => (childrenMap.get(id)?.length || 0) > 0)
      .flatMap((id) => childrenMap.get(id) || []);
    return new Set(disabledChildrenIds);
  }, [selectedItems, childrenMap, selectionRules]);

  // Handler for selection changes
  const handleSelectionChange = useCallback(
    (newSelectedItems: string[]) => {
      // MUI's selectionPropagation handles cascading, so we just pass through
      setSelectedItems(newSelectedItems);

      // Use the reusable filter function
      const outputItems = filterParentNodes(newSelectedItems);
      onSelectionChange?.(outputItems);
    },
    [onSelectionChange, filterParentNodes]
  );

  // Handle item deletion - optimized to use functional update
  const handleItemDelete = useCallback(
    (itemId: string) => {
      const item = itemsMap.get(itemId);
      if (!item) return;

      setSelectedItems((prevSelected) => {
        const itemsToRemove = [itemId];

        // If cascading is enabled, also remove all children
        if (selectionRules.cascadeChildren) {
          const descendants = getAllDescendants(itemId);
          if (descendants.length > 0) itemsToRemove.push(...descendants);
        }

        // Filter out the items to remove
        const newSelection = prevSelected.filter(
          (id) => !itemsToRemove.includes(id)
        );

        // Use the reusable filter function
        const outputItems = filterParentNodes(newSelection);

        // Notify parent component if selection changed
        if (newSelection.length !== prevSelected.length) {
          onSelectionChange?.(outputItems);
        }

        return newSelection;
      });
    },
    [
      itemsMap,
      getAllDescendants,
      onSelectionChange,
      selectionRules,
      filterParentNodes,
    ]
  );

  // Check if an item should be disabled
  const isItemDisabled = useCallback(
    (item: TreeViewBaseItem): boolean => {
      return disabledItemsSet.has(item.id);
    },
    [disabledItemsSet]
  );

  // Get item label from ID
  const getItemLabel = useCallback(
    (itemId: string): string => {
      const item = itemsMap.get(itemId);
      return item?.label?.toString() || itemId;
    },
    [itemsMap]
  );

  // Get display items that should be shown in the UI (chips)
  const displayItems = useMemo(() => {
    // Check if we should collapse chips to ancestors
    if (!selectionRules.collapseChipsToAncestors) {
      // When not collapsing, show the output items (filtered or not based on rules)
      return outputSelectedItems;
    }
    // Collapse to highest ancestors to avoid redundancy
    // Use outputSelectedItems to respect the excludeParentsFromSelection rule
    return filterToHighestAncestors(outputSelectedItems);
  }, [outputSelectedItems, filterToHighestAncestors, selectionRules]);

  return {
    selectedItems,
    displayItems,
    handleSelectionChange,
    handleItemDelete,
    isItemDisabled,
    disabledItemsSet,
    itemsMap,
    getAllDescendants,
    getItemLabel,
  };
}
